<script setup>
  import {ref, watch} from "vue";

  import ComponentDialog from "@components/Dialogs/Dialog.vue";
  import {useForm} from "@inertiajs/vue3";
  import {MedButton} from "@pharma4u/medicheck-components";

  defineOptions({
    name: "ComponentRecordShareRevokeDialog",
  });

  const props = defineProps({
    record: {
      type: Object,
      default: () => {
        return null;
      },
    },
    notification: {
      type: Object,
      default: () => {
        return null;
      },
    },
  });

  const emit = defineEmits(["revoked"]);

  const refDialog = ref(null);

  const currentRecord = ref(props.record);
  const currentNotification = ref(props.notification);

  let form = useForm({});

  watch(
    () => props.record,
    (record) => {
      currentRecord.value = record;
    },
  );

  function open(notification) {
    if (notification !== undefined) {
      form.reset();
      form.wasSuccessful = false;
      currentNotification.value = notification;
    }
    refDialog.value.open();
  }

  function close() {
    refDialog.value.close();
  }

  function revoke() {
    form.delete(
      route("sharedrecords.destroy", {
        patient: currentRecord.value.patient_id,
        record: currentRecord.value.id,
        sharedrecord: currentNotification.value.sharedrecord_id,
      }),
      {
        only: ["flash", "errors"],
        preserveScroll: true,
        onSuccess: () => {
          emit("revoked");
        },
      },
    );
  }

  defineExpose({
    open,
    close,
  });
</script>

<template>
  <ComponentDialog
    ref="refDialog"
    dialog-width="small"
    :dialog-title="form.wasSuccessful ? 'Widerruf erfolgreich' : 'Sind Sie sicher?'"
    :test-id="getTestId('revoke')"
    :is-loading="form.processing"
  >
    <template #content>
      <div v-if="form.wasSuccessful">
        <span v-if="currentNotification.audience === 'group'">Der Seminarlink wurde deaktiviert.</span>
        <span v-else>Die Freigabe wurde dem Empfänger wieder entzogen.</span>
      </div>

      <div v-else>
        <template v-if="currentNotification.audience === 'group'">
          <p>
            Wenn Sie die Freigabe widerrufen, wird der Seminarlink deaktiviert. Bisher damit geteilte Fälle bleiben den
            Teilnehmern erhalten.
          </p>

          <p class="mt-4">Wenn Sie das nicht möchten, können Sie zusätzlich die Fälle einzeln widerrufen.</p>
        </template>

        <template v-else>
          <p>Wenn Sie die Freigabe widerrufen, hat der Empfänger keinen Zugriff mehr auf diese Variante.</p>
        </template>
      </div>
    </template>

    <template #actions>
      <template v-if="!form.wasSuccessful">
        <MedButton
          variant="primary"
          :disabled="form.processing"
          :data-test="getTestId('revoke-dialog-revoke-button')"
          @click="revoke()"
        >
          Widerrufen
        </MedButton>
      </template>

      <MedButton :data-test="getTestId('revoke-dialog-close-button')" @click="close()">
        <template v-if="form.wasSuccessful">Schließen</template>
        <template v-else>Abbrechen</template>
      </MedButton>
    </template>
  </ComponentDialog>
</template>
